import { FC, ReactNode, memo } from "react";
import Link from "next/link";

import {
  designClassMap,
  ButtonComponent,
  Color,
  Design,
  Type,
  Size,
  sizeClassMap,
} from "apps/website/components/base/Button/Button.map";
import { DisplayState } from "@/constants/state";
import { WithTestID } from "apps/website/types";
import cx from "apps/website/utils/misc/cx";

import Icon from "../Icon/Icon";
import TextControl from "../Text/TextControl/TextControl";

import ButtonState from "./ButtonState";

export interface IButton extends WithTestID {
  state?: DisplayState;
  id?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
  to?: string;
  type?: Type;
  size?: Size;
  design?: Design;
  color?: Color;
  disabled?: boolean;
  onClick?: () => void;
  forceHardReload?: boolean;
  className?: string;
  component?: string;
  children: ReactNode;
  withIcon?: boolean; // Relevant for links
}

export interface IButtonIcon {
  to?: string;
}

export interface IButtonAnchorTag {
  state: DisplayState;
  disabled?: boolean;
  children: ReactNode;
  withIcon?: boolean; // Relevant for links
  isExternalLink?: boolean;
  link?: string;
  classes: string;
  id?: string;
  testId?: string;
  onClick?: () => void;
  size?: Size;
  to?: string;
  component?: string;
}

export interface IWrappedOrUnwrappedButtonAnchorTag extends IButtonAnchorTag {
  wrapped: boolean;
  nextLink: string;
}

const isLinkExternal = (to?: string) => {
  const startsWithHashOrQuery = to?.startsWith("#") || to?.startsWith("?");
  const isRelativePath = to?.startsWith("/") || startsWithHashOrQuery;
  return to?.startsWith("/manage-plan")
    || !isRelativePath;
};

const ButtonIcon: FC<IButtonIcon> = ({ to }) => {
  if (!to) { return <></>; }
  return (
    <div className={cx("h-5 w-5 flex items-center justify-center ml-2")}>
      <Icon size="fit" icon={ to.startsWith("#") ? "arrowThinDown" : "arrowRightShort" } color="inherit" />
    </div>
  );
};

const AnchorTag: FC<IButtonAnchorTag> = ({
  state, disabled, children, withIcon, isExternalLink, link, classes, id, testId, onClick, size, to, component = "Button",
}) => {
  const target = isExternalLink ? "_blank" : undefined;
  const rel = isExternalLink ? "nofollow noopener" : undefined;
  return (
    <a
      data-component={ component }
      href={ link }
      data-disabled={ disabled }
      target={ target }
      rel={ rel }
      className={ classes }
      id={ id }
      data-testid={ testId }
      onClick={ onClick }
    >
      <TextControl tag="span" size={ size === "large" ? 1 : 2 } color="inherit" className="flex justify-center items-center">
        <ButtonState state={ state }>{ children }{ withIcon ? <ButtonIcon to={ to } /> : null }</ButtonState>
      </TextControl>
    </a>
  );
};

const WrappedOrUnWrappedAnchorTag: FC<IWrappedOrUnwrappedButtonAnchorTag> = ({
  state,
  disabled,
  children,
  withIcon,
  isExternalLink,
  link,
  classes,
  id, testId,
  onClick,
  size,
  to,
  wrapped,
  nextLink,
  component,
}) => (
  <>
    { wrapped ? (
      <Link href={ nextLink } legacyBehavior>
        <AnchorTag
          state={ state }
          disabled={ disabled }
          withIcon={ withIcon }
          isExternalLink={ isExternalLink }
          link={ link }
          classes={ classes }
          id={ id }
          testId={ testId }
          onClick={ onClick }
          size={ size }
          to={ to }
          component={ component }
        >
          { children }
        </AnchorTag>
      </Link>
    ) : (
      <AnchorTag
        state={ state }
        disabled={ disabled }
        withIcon={ withIcon }
        isExternalLink={ isExternalLink }
        link={ link }
        classes={ classes }
        id={ id }
        testId={ testId }
        onClick={ onClick }
        size={ size }
        to={ to }
        component={ component }
      >
        { children }
      </AnchorTag>
    ) }
  </>
);

const Button: FC<IButton> = ({
  color = "default",
  id,
  "data-testid": testId,
  ariaControls,
  ariaExpanded,
  state = DisplayState.READY,
  design = "default",
  to,
  type = "button",
  size = "default",
  disabled = false,
  onClick,
  className = "",
  component = "Button",
  forceHardReload = false,
  withIcon = false,
  children,
}) => {

  const componentElement: ButtonComponent = to ? ButtonComponent.LINK : ButtonComponent.DEFAULT;
  const computedClasses = `${designClassMap[design].base} ${designClassMap[design][color]} ${sizeClassMap[size]} ${className}`;

  // Link
  const link = disabled ? "" : to;
  const nextHref = link ?? "";
  const showUnwrappedAnchorTag = isLinkExternal(to) || !to || forceHardReload;

  return <>
    { componentElement === ButtonComponent.DEFAULT && (
      <button
        data-component={ component }
        id={ id }
        data-testid={ testId }
        type={ type }
        className={ computedClasses }
        disabled={ disabled }
        onClick={ onClick }
        aria-controls={ ariaControls }
        aria-expanded={ ariaExpanded }
      >
        <TextControl tag="span" size={ size === "large" ? 1 : 2 } color="inherit" align="center" className="flex justify-center items-center">
          <ButtonState state={ state }>{ children }</ButtonState>
        </TextControl>
      </button>
    ) }
    { componentElement === ButtonComponent.LINK && (
      <WrappedOrUnWrappedAnchorTag
        state={ state }
        disabled={ disabled }
        withIcon={ withIcon }
        isExternalLink={ isLinkExternal(to) }
        link={ link }
        classes={ computedClasses }
        id={ id }
        testId={ testId }
        onClick={ onClick }
        size={ size }
        to={ to }
        nextLink={ nextHref }
        wrapped={ !showUnwrappedAnchorTag }
        component={ component }
      >
        { children }
      </WrappedOrUnWrappedAnchorTag>
    ) }
  </>;
};

export default memo(Button);
